<!--
  ~ Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
  ~ This information is confidential and proprietary to GM Global Technology
  ~ Operations LLC and may not be used, modified, copied or distributed.
  -->
<div #ganttChartRootElement style="position: relative; width: 100%; height: 100%;"> <!-- This DIV contains everything including the scrollbars on the side and bottom of the chart. -->
    <div [ngStyle]="{
      'width': (displayWidth + (trueSize ? 0: 16)) + 'px',
      'height': (displayHeight  + (trueSize ? 0: 16)) + 'px',
      'overflow-y': 'hidden', 'overflow-x': 'hidden'}">
    <!-- This DIV contain everything that gets captured by the domToImage for including as a screenshot. -->
        <div [ngStyle]="{
          'width': (displayWidth + (trueSize ? 0: 16)) + 'px',
          'height': (displayHeight  + (trueSize ? 0: 16)) + 'px',
          'overflow-y': 'hidden', 'overflow-x': 'hidden'}">
      <svg #ganttChartSvgElement xmlns="http://www.w3.org/2000/svg"
        [attr.width]='displayWidth' [attr.height]='displayHeight'>
        <rect x='0' y='0' [attr.width]='displayWidth' [attr.height]='displayHeight' fill='#EEEEEE'/>
        <g ng-if="ganttChart">
          <g>
            <g ng-if="startDate">
              <line stroke='#FF0000' stroke-width='3' opacity=".7"
                [attr.x1]='getDateX(startDate)' y1='0'
                [attr.x2]='getDateX(startDate)' [attr.y2]='displayHeight'/>
                <text [attr.x]='getDateX(startDate) + 2 + dateFontSize * textScale' [attr.y]='displayHeight/2'
                  text-anchor="middle" alignment-baseline="middle"
                  [attr.font-size]='dateFontSize * textScale' fill="#FF0000"
                [attr.transform]="'rotate(-90, ' + (getDateX(startDate) + 2 + dateFontSize * textScale) + ', ' + displayHeight/2 + ')'">Start: {{getDateYearStr(startDate)}}</text>
              </g>
              <g ng-if="endDate">
                <line stroke='#FF0000' stroke-width='3' opacity=".7"
                  [attr.x1]='getDateX(endDate)' y1='0'
                  [attr.x2]='getDateX(endDate)' [attr.y2]='displayHeight'/>
                  <text [attr.x]='getDateX(endDate) - 2' [attr.y]='displayHeight/2'
                    text-anchor="middle" alignment-baseline="baseline"
                    [attr.font-size]='dateFontSize * textScale' fill="#FF0000"
                  [attr.transform]="'rotate(-90, ' + (getDateX(endDate) - 2) + ', ' + displayHeight/2 + ')'">End: {{getDateYearStr(endDate)}}</text>
                </g>

                <line stroke='#FF0000' [attr.stroke-width]='2*scale'
                  [attr.x1]='getDateX(today)' y1='0'
                  [attr.x2]='getDateX(today)' [attr.y2]='displayHeight'/>
                  @for (days of highlightDayWindows; track days) {
                    <g>
                      <rect stroke='#FF0000' [attr.stroke-width]='1' opacity=".2" fill="#FF0000"
                        [attr.x]='getDateX(today)' y='0'
                        [attr.width]='pixelsPerDay * days * scale' [attr.height]='displayHeight'/>
                      </g>
                    }
                    @for (data of upcomingWindows; track data.label) {
                      <g>
                        @for (yFactor of labelYFactors; track yFactor) {
                          <g>
                            <text fill='#000000' text-anchor="middle" alignment-baseline="middle"
                              [attr.x]='getDateX(today)+(pixelsPerDay * data.days * scale - 1)'
                              [attr.y]='displayHeight * yFactor / 4' [attr.font-size]='dateFontSize * textScale * 2'
                              [attr.transform]="'rotate(-90, ' + (getDateX(today)+(pixelsPerDay * data.days * scale - 1)) + ', ' + (displayHeight * yFactor / 4) + ')'">
                            {{data.label}}</text>
                          </g>
                        }
                      </g>
                    }

                    <text [attr.x]='getDateX(today)' y='200' text-anchor='middle' fill="#500000"
                      alignment-baseline="central"
                      [attr.font-size]='(dateFontSize * textScale * scale) > 32 ? 32 : (dateFontSize * textScale * scale)'
                    [attr.transform]="'rotate(-90, ' + getDateX(today) + ', 200)'">Today: {{getDateStr(today)}}</text>
                  </g>
                  <!-- Print the backgrounds for the sections before we put down the monday RECTs, so we can properly
                  capture the on click when the user clicks on a week. -->
                  @if (ganttChart?.showSectionBackgrounds) {
                    <ng-container>
                      @for (ganttSection of ganttChart?.ganttSections || []; track ganttSection.label + '-' + $index) {
                        <g>
                          <rect stroke='#444444' stroke-width='1.5' opacity=".5"
                            [attr.fill]="ganttChart.ganttSections.indexOf(ganttSection) % 2 == 0 ? '#FFFFFF' : '#DDDDDD'"
                            [attr.rx]='(rowHeight/3)*scale'
                            [attr.ry]='(rowHeight/3)*scale'
                            [attr.x]='getDateX(ganttSection.ganttItems[0].startDate)'
                            [attr.y]='ganttSection.ganttItems[0].rowPosition * rowHeight * scale + yOffset'
                            [attr.width]='getDateX(ganttSection.ganttItems[0].getDateEnd()) - getDateX(ganttSection.ganttItems[0].getDateStart())'
                            [attr.height]='(ganttSection.ganttItems[ganttSection.ganttItems.length-1].rowPosition - ganttSection.ganttItems[0].rowPosition + 1) * rowHeight * scale'/>
                          </g>
                        }
                      </ng-container>
                    }

                    @for (monday of mondays; track monday.getTime()) {
                      <g>
                        <!-- Draw the vertical lines for the mondays -->
                        <line stroke='#0072ce' [attr.stroke-width]='.2*scale' stroke-dasharray="2,2"
                          [attr.opacity]="(scale > .707) ? '.2' : '.05'"
                          [attr.x1]='getDateX(monday)' y1='0'
                          [attr.x2]='getDateX(monday)' [attr.y2]='displayHeight'/>
                          <!-- Draw the background for the mondays, and serve as a click point to highlight a week -->
                          <rect [attr.x]='getDateX(monday)' y='0'
                            fill='#000000'
                            [attr.opacity]="selectedColumn === monday ? '.2' : '0'"
                            [attr.width]='pixelsPerDay * 7 * scale - 1'
                            [attr.height]='displayHeight'
                            (click)="selectColumn(monday)"/>
                          </g>
                        }

                        @for (yFactor of labelYFactors; track yFactor) {
                          <g>
                            <text fill='#FFFFFF' text-anchor="middle" alignment-baseline="middle" ng-if="selectedColumn"
                              [attr.x]='getDateX(selectedColumn) + (pixelsPerDay * 7 * scale - 1)/2'
                              [attr.y]='displayHeight * yFactor / 4'
                              [attr.font-size]='(dateFontSize * textScale * scale) > 64 ? 32 : (scale > 2 ? (dateFontSize * textScale * scale / 2) : dateFontSize * textScale)'
                              [attr.transform]="'rotate(-90, ' + (getDateX(selectedColumn) + (pixelsPerDay * 7 * scale - 1)/2) + ', ' + (displayHeight * yFactor / 4) + ')'">
                            {{getDateStr(selectedColumn) + ' - ' + getDateStr(selectedColumn, 1)}}</text>
                          </g>
                        }

                        @for (month of months; track month.getTime()) {
                          <g>
                            <!-- Draw the vertical lines for the months -->
                            <line stroke='#0072ce' [attr.stroke-width]='.4*scale' stroke-dasharray="5,5" opacity=".4"
                              [attr.x1]='getDateX(month)' y1='0'
                              [attr.x2]='getDateX(month)'
                              [attr.y2]='displayHeight - 4.5*dateFontSize * textScale'/>
                              <!-- Write the month text at the bottom, above where the scrollbar (10px) should be, if present.-->
                              <text [attr.x]='getDateX(month) + dateFontSize * textScale/2'
                                [attr.y]='displayHeight - 10'
                                [attr.font-size]='dateFontSize * textScale' fill="#0072ce"
                                [attr.transform]="'rotate(-90, ' + (getDateX(month)+dateFontSize * textScale/2) + ', ' + (displayHeight - 10) + ')'"
                              >{{getMonYearStr(month)}}</text>
                            </g>
                          }
                          @for (ganttSection of ganttChart?.ganttSections || []; track ganttSection.label + '-' + $index) {
                            <g>
                              @if (ganttChart?.showSpecialDates !== false) {
                                <ng-container>
                                  @for (date of ganttSection.specialDates; track date.label + '-' + date.date.getTime() + '-' + $index) {
                                    <g>
                                      <!-- draw the diamond shape for the date -->
                                      <rect stroke='#444444' stroke-width='.5' opacity=".5"
                                        [attr.fill]="date.label.includes('WHA') ? '#8888FF' : '#00FF00'" rx=0 ry=0
                                        [attr.x]='getDateX(date.date) + pixelsPerDay * scale / 2'
                                        [attr.y]='(ganttSection.ganttItems[0].rowPosition + 2) * rowHeight * scale + yOffset'
                                        [attr.width]='rowHeight * scale * 1.414'
                                        [attr.height]='rowHeight * scale * 1.414'
                                        [attr.transform]="'rotate(45, ' + (getDateX(date.date) + pixelsPerDay * scale / 2) + ', ' + ((ganttSection.ganttItems[0].rowPosition + 2) * rowHeight * scale + yOffset) + ')'"
                                        ><title>{{date.tooltip}}</title></rect>
                                        @if (ganttChart?.showSpecialDateLines !== false) {
                                          <!-- draw the vertical line for the date -->
                                          <rect stroke='#444444' stroke-width='.5' opacity=".5"
                                            [attr.fill]="date.label.includes('WHA') ? '#8888FF' : '#00FF00'" rx=0 ry=0
                                            [attr.x]='getDateX(date.date)'
                                            [attr.y]='(ganttSection.ganttItems[0].rowPosition + 4) * rowHeight * scale + yOffset - pixelsPerDay * scale / 2'
                                            [attr.width]='pixelsPerDay * scale'
                                            [attr.height]='(ganttSection.ganttItems[ganttSection.ganttItems.length-1].rowPosition - ganttSection.ganttItems[0].rowPosition - 3) * rowHeight * scale'
                                            ><title>{{date.tooltip}}</title></rect>
                                        }
                                          <text fill='#000000' text-anchor="middle" alignment-baseline="middle" ng-if="selectedColumn"
                                            [attr.x]='getDateX(date.date)'
                                            [attr.y]='(ganttSection.ganttItems[0].rowPosition + 3) * rowHeight * scale + yOffset'
                                            [attr.width]='pixelsPerDay * scale'
                                            [attr.height]='pixelsPerDay * scale'
                                            [attr.font-size]='dateFontSize * scale'
                                            >{{date.label}}{{ganttChart?.showSpecialDateValue ? (' ' + getDateStr(date.date)) : ''}}<title>{{date.tooltip}}</title></text>
                                          </g>
                                        }
                                      </ng-container>
                                    }
                                    @if (ganttChart?.showGanttItems) {
                                      <ng-container>
                                        @for (ganttItem of ganttSection.ganttItems; track ganttItem.taskId) {
                                          <g>
                                            <rect #rectElement stroke='#444444' stroke-width='1.5'
                                              [attr.id]="'gantt_rect_'+ganttItem.taskId"
                                              [attr.fill]="ganttItem.getFillColor()"
                                              [attr.opacity]="ganttItem.getOpacity()"
                                              [attr.rx]='(rowHeight/3)*scale'
                                              [attr.ry]='(rowHeight/3)*scale'
                                              [attr.x]='getDateX(ganttItem.getDateStart())'
                                              [attr.y]='ganttItem.rowPosition * rowHeight * scale + yOffset'
                                              [attr.width]='getDateX(ganttItem.getDateEnd()) - getDateX(ganttItem.getDateStart())'
                                              [attr.height]='rowHeight*scale*ganttItem.vSpan'>
                                              <title>{{ganttItem.getTooltip()}}</title>
                                            </rect>
                                            <text #textInsideElement text-anchor="middle" alignment-baseline="middle"
                                              [attr.opacity]="ganttItem.textLoc === 'Inside' ? '1' : '0'"
                                              [attr.id]="'gantt_text_inside_'+ganttItem.taskId"
                                              [attr.x]='getDateX(ganttItem.getDateStart()) + (getDateX(ganttItem.getDateEnd()) - getDateX(ganttItem.getDateStart()))/2'
                                              [attr.y]='ganttItem.rowPosition * rowHeight * scale + yOffset + rowHeight*scale*ganttItem.vSpan/2'
                                              [attr.font-size]='dateFontSize * scale * ganttItem.vSpan'
                                              [attr.fill]="ganttItem.getTextColor()">{{ganttItem.label}}
                                              <title>{{ganttItem.getTooltip()}}</title>
                                            </text>
                                            <text text-anchor="start" alignment-baseline="middle"
                                              [attr.opacity]="ganttItem.textLoc === 'Right' ? '1' : '0'"
                                              [attr.id]="'gantt_text_right_'+ganttItem.taskId"
                                              [attr.x]='getDateX(ganttItem.getDateStart()) + (getDateX(ganttItem.getDateEnd()) - getDateX(ganttItem.getDateStart()))'
                                              [attr.y]='ganttItem.rowPosition * rowHeight * scale + yOffset + rowHeight*scale/2'
                                              [attr.font-size]='dateFontSize * scale'
                                              [attr.fill]="ganttItem.getTextColor()">{{ganttItem.label}}
                                              <title>{{ganttItem.getTooltip()}}</title>
                                            </text>
                                          </g>
                                        }
                                      </ng-container>
                                    }
                                  </g>
                                }
                                <!-- put the text (on a fresh background with vertical lines) for the mondays -->
                                <!-- ideally, this block below would only be shown when the user is zoomed in to scale of 1 or more,
                                but for some reason, using a <g ng-if=""> wrapper doesn't do anything, so instead we set the
                                opacity to 0 when this block shouldn't be shown:-->
                                <rect x='0' y='0' fill='#EEEEEE'
                                  [attr.width]='displayWidth' [attr.height]='dateFontSize * textScale * 4'
                                  [attr.opacity]="(trueSize || (scale > .99)) ? '1' : '0'"/>
                                  @for (monday of mondays; track monday.getTime()) {
                                    <g>
                                      <line stroke='#0072ce' [attr.stroke-width]='.2*scale' stroke-dasharray="2,2"
                                        [attr.opacity]="(scale > .707) ? '.2' : '.05'"
                                        [attr.x1]='getDateX(monday)' y1='0'
                                        [attr.x2]='getDateX(monday)' [attr.y2]='dateFontSize * textScale * 4'/>
                                        <rect [attr.x]='getDateX(monday)' y='0'
                                          fill='#000000'
                                          [attr.opacity]="selectedColumn === monday ? '.2' : '0'"
                                          [attr.width]='pixelsPerDay * 7 * scale - 1'
                                          [attr.height]='dateFontSize * textScale * 4'
                                          (click)="selectColumn(monday)"/>
                                          <text fill='#0072ce' text-anchor="end" alignment-baseline="central"
                                            [attr.opacity]="(trueSize || (scale > .99)) ? '.5' : '0'"
                                            [attr.x]='getDateX(monday) + dateFontSize * textScale / 2'
                                            [attr.y]='dateFontSize * textScale / 2'
                                            [attr.font-size]='dateFontSize * textScale'
                                            [attr.transform]="'rotate(-90, ' + (getDateX(monday) + dateFontSize * textScale / 2) + ', ' + (dateFontSize * textScale / 2) + ')'"
                                          >{{getDateStr(monday)}}</text>
                                        </g>
                                      }
                                    </g>
                                  </svg>
                                </div>
                              </div>

                              @if (!trueSize) {
                                <div #scrollbarVertical
                                  (scroll)="onVerticalScrollbarScroll($event)"
                                  style="position: absolute; top: 0; right: 0; width: 16px; height: calc(100% - 16px); overflow-y: scroll; overflow-x: hidden;">
                                  <div [ngStyle]="{'width': '1px', 'height': yExtent * scale + 'px', 'background-color': '#EEEEEE'}"></div>
                                </div>
                              }
                              @if (!trueSize) {
                                <div #scrollbarHorizontal
                                  (scroll)="onHorizontalScrollbarScroll($event)"
                                  style="position: absolute; bottom: 0; left: 0; width: calc(100% - 16px); height: 16px; overflow-x: scroll; overflow-y: hidden;">
                                  <div [ngStyle]="{'height': '1px', 'width': xExtent * scale + 'px', 'background-color': '#EEEEEE'}"></div>
                                </div>
                              }
                            </div>
